import { useTranslation } from 'react-i18next';

import { Checkbox, Select } from '@/components';

import { AnimationPicker } from '../controls/AnimationPicker';
import { NumberInput } from '../controls/NumberInput';
import { EASING_OPTIONS } from '../styleConstants';
import type { HoverEffect, LayoutElementStyle } from '../styleTypes';

interface AnimationsSectionProps {
  style: LayoutElementStyle;
  onChange: (updates: Partial<LayoutElementStyle>) => void;
}

export function AnimationsSection({ style, onChange }: AnimationsSectionProps) {
  const { t } = useTranslation();
  const p = 'tenant.systemModuleBuilder.style.';
  const hover = style.hoverEffect || {};
  const hasHover = !!style.hoverEffect;

  const updateHover = (updates: Partial<HoverEffect>) => {
    onChange({ hoverEffect: { ...hover, ...updates } });
  };

  return (
    <div className="space-y-4">
      {/* Entry Animation */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-2">
          {t(`${p}animations.entry`)}
        </label>
        <AnimationPicker
          value={style.entryAnimation}
          onChange={(v) => onChange({ entryAnimation: v })}
        />
      </div>

      {/* Animation Settings */}
      {style.entryAnimation && style.entryAnimation !== 'none' && (
        <div className="space-y-2 p-2.5 bg-gray-50 dark:bg-gray-900 rounded-lg">
          <div className="flex flex-wrap gap-2">
            <NumberInput
              value={style.animationDuration}
              onChange={(v) => onChange({ animationDuration: v })}
              min={100}
              max={3000}
              step={50}
              label={t(`${p}animations.duration`)}
              unit="ms"
              className="min-w-[7rem] flex-1"
            />
            <NumberInput
              value={style.animationDelay}
              onChange={(v) => onChange({ animationDelay: v })}
              min={0}
              max={2000}
              step={50}
              label={t(`${p}animations.delay`)}
              unit="ms"
              className="min-w-[7rem] flex-1"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
              {t(`${p}animations.easing`)}
            </label>
            <Select
              value={style.animationEasing || 'ease-out'}
              onChange={(e) =>
                onChange({
                  animationEasing: e.target.value as LayoutElementStyle['animationEasing'],
                })
              }
              className="px-2 py-1.5 text-xs"
            >
              {EASING_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {/* Hover Effect */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] text-gray-400 uppercase tracking-wider">
            {t(`${p}animations.hover`)}
          </label>
          <Checkbox
            checked={hasHover}
            onChange={(e) =>
              onChange({
                hoverEffect: e.target.checked
                  ? { scale: 1.02, transitionDuration: 200 }
                  : undefined,
              })
            }
            label={<span className="text-[10px] text-gray-400">{t(`${p}common.enable`)}</span>}
            className="h-3 w-3"
          />
        </div>
        {hasHover && (
          <div className="space-y-2 p-2.5 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="flex flex-wrap gap-2">
              <NumberInput
                value={hover.scale}
                onChange={(v) => updateHover({ scale: v })}
                min={0.8}
                max={1.5}
                step={0.01}
                label={t(`${p}animations.scale`)}
                unit="×"
                className="min-w-[7rem] flex-1"
              />
              <NumberInput
                value={hover.translateY}
                onChange={(v) => updateHover({ translateY: v })}
                min={-20}
                max={20}
                label={t(`${p}animations.translateY`)}
                unit="px"
                className="min-w-[7rem] flex-1"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <NumberInput
                value={hover.brightness}
                onChange={(v) => updateHover({ brightness: v })}
                min={50}
                max={150}
                label={t(`${p}animations.brightness`)}
                unit="%"
                className="min-w-[7rem] flex-1"
              />
              <NumberInput
                value={hover.opacity}
                onChange={(v) => updateHover({ opacity: v })}
                min={0}
                max={100}
                label={t(`${p}common.opacity`)}
                unit="%"
                className="min-w-[7rem] flex-1"
              />
            </div>
            <NumberInput
              value={hover.transitionDuration}
              onChange={(v) => updateHover({ transitionDuration: v })}
              min={50}
              max={1000}
              step={50}
              label={t(`${p}animations.transitionDuration`)}
              unit="ms"
            />
          </div>
        )}
      </div>
    </div>
  );
}
